"use client";

import { AnimatePresence, motion } from "framer-motion";
import type { JSX } from "react";
import { useState } from "react";
import {
  isDataUIPart,
  isReasoningUIPart,
  isTextUIPart,
  isToolUIPart,
} from "ai";
import type { UseChatHelpers } from "@ai-sdk/react";
import {
  parseRuntimeContextText,
  runtimeContextDataPartSchema,
  type ChatMessage,
  type TaggedContextItem,
} from "@tracify/shared";
import { sanitizeText } from "@/lib/ai/utils";
import { cn } from "@/lib/utils";
import {
  Message as AIMessage,
  MessageContent,
  MessageResponse,
} from "@/components/ai-elements/message";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { ChevronDownIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@tracify/ui/dialog";
import { ChatMessageActions } from "./chat-message-actions";
import { ChatMessageEditor } from "./chat-message-editor";
import { AssistantThinkingMessage } from "./ui/assistant-loader";
import { ActionToolGroup, OutputToolGroup } from "./ui/tool-artifact-card";
import { ToolPhaseGroup } from "./ui/tool-phase-group";
import { ToolCallCard } from "./ui/tool-call-card";
import { AskUserQuestionSummary } from "./ui/ask-user-question-summary";
import { getToolDisplayName } from "@/components/ai-elements/tool-call";
import { getAskUserQuestionTranscriptModel } from "./ask-user-question";
import {
  getPendingToolCallApprovalMap,
  type ToolCallApprovalActionInput,
} from "./tool-call-approval";

type ToolPart = Extract<
  ChatMessage["parts"][number],
  { type: `tool-${string}` }
>;
type FilePart = Extract<ChatMessage["parts"][number], { type: "file" }>;

type ToolCategory = "read" | "mutation" | "output" | "action";
type GroupedToolCategory = Exclude<ToolCategory, "mutation">;

type ToolDisplayConfig = {
  title?: string;
  category?: ToolCategory;
};

const TOOL_DISPLAY_CONFIG: Record<string, ToolDisplayConfig> = {
  getAnalyticsSnapshot: { title: "Analytics snapshot" },
  getKpisOverview: { title: "KPIs overview" },
  getKpisPerChannel: { title: "KPIs by channel" },
  getChannelKpiBreakdown: { title: "Channel KPI breakdown" },
  getCustomerJourneys: { title: "Customer journeys" },
  getInsightConfig: { title: "Load insight configuration" },
  executeInsight: { title: "Run insight" },
  listInsights: { title: "List insights" },
  listAttributionPresets: { title: "List attribution presets" },
  listDashboards: { title: "List dashboards" },
  createDashboard: { title: "Create dashboard", category: "mutation" },
  updateDashboard: { title: "Update dashboard", category: "mutation" },
  deleteDashboard: { title: "Delete dashboard", category: "mutation" },
  createInsight: { title: "Create insight", category: "mutation" },
  updateInsight: { title: "Update insight", category: "mutation" },
  addInsightToDashboard: {
    title: "Add insights to dashboard",
    category: "mutation",
  },
  removeInsightFromDashboard: {
    title: "Remove insights from dashboard",
    category: "mutation",
  },
  saveOutput: { title: "Save output", category: "output" },
  generateImage: { title: "Generate image", category: "output" },
  proposeAction: { title: "Propose action", category: "action" },
  askUserQuestion: { title: "Question" },
};

function getToolKey(part: ToolPart) {
  return part.type.replace(/^tool-/, "");
}

function getToolDisplayConfig(part: ToolPart): Required<ToolDisplayConfig> {
  const toolKey = getToolKey(part);
  const config = TOOL_DISPLAY_CONFIG[toolKey];

  return {
    title: config?.title ?? getToolDisplayName(part.type),
    category: config?.category ?? "read",
  };
}

function getToolTitle(part: ToolPart) {
  return getToolDisplayConfig(part).title;
}

function getToolCategory(part: ToolPart) {
  return getToolDisplayConfig(part).category;
}

function shouldRenderStandaloneToolPart(part: ToolPart) {
  return getToolKey(part) === "askUserQuestion";
}

function getTaggedContextFromPart(
  part: ChatMessage["parts"][number]
): TaggedContextItem[] {
  if (part.type === "data-runtimeContext") {
    const parsed = runtimeContextDataPartSchema.safeParse(part);
    return parsed.success ? (parsed.data.data.tagged ?? []) : [];
  }

  if (part.type === "text") {
    return parseRuntimeContextText(part.text)?.tagged ?? [];
  }

  return [];
}

function getTaggedContext(parts: ChatMessage["parts"] | undefined) {
  if (!parts || parts.length === 0) return null;

  const tagged = parts.flatMap(getTaggedContextFromPart);

  const dedupe = (items: TaggedContextItem[]) => {
    const seen = new Set<string>();
    return items.filter((item) => {
      const key = `${item.type}:${item.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const dedupedTagged = dedupe(tagged);

  if (dedupedTagged.length === 0) {
    return null;
  }

  return dedupedTagged;
}

type ContextTagProps = {
  kind: "Dashboard" | "Insight";
  label: string;
};

type AssistantTextSegment = {
  kind: "text";
  key: string;
  text: string;
};

type AssistantThinkingPhaseSegment = {
  kind: "thinking";
  key: string;
};

type AssistantReadToolGroupSegment = {
  kind: "tool-group";
  key: string;
  category: GroupedToolCategory;
  parts: ToolPart[];
  isActive: boolean;
};

type AssistantToolSegment = {
  kind: "tool";
  key: string;
  part: ToolPart;
  isActive: boolean;
};

type AssistantAskUserSummarySegment = {
  kind: "ask-user-summary";
  key: string;
  model: ReturnType<typeof getAskUserQuestionTranscriptModel>;
};

type AssistantSegment =
  | AssistantTextSegment
  | AssistantThinkingPhaseSegment
  | AssistantReadToolGroupSegment
  | AssistantToolSegment
  | AssistantAskUserSummarySegment;

type AssistantPhaseState = {
  key: string | null;
  hasReasoning: boolean;
  category: GroupedToolCategory | null;
  toolParts: ToolPart[];
};

const ContextTag = ({ kind, label }: ContextTagProps) => {
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-2 rounded-xl border border-border bg-card px-2.5 py-1.5 text-xs text-card-foreground shadow-sm">
      <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {kind}
      </span>
      <span className="max-w-[14rem] truncate font-medium sm:max-w-[20rem]">
        {label}
      </span>
    </span>
  );
};

function formatToolCount(count: number) {
  return `${count} tool${count === 1 ? "" : "s"}`;
}

function isImageFilePart(
  part: ChatMessage["parts"][number] | undefined
): part is FilePart {
  return Boolean(
    part &&
    part.type === "file" &&
    typeof part.mediaType === "string" &&
    part.mediaType.startsWith("image/") &&
    typeof part.url === "string" &&
    part.url.trim().length > 0
  );
}

function getImageFileLabel(part: FilePart, index: number) {
  if ("name" in part && typeof part.name === "string" && part.name.trim()) {
    return part.name.trim();
  }

  if (
    "filename" in part &&
    typeof part.filename === "string" &&
    part.filename.trim()
  ) {
    return part.filename.trim();
  }

  return `Uploaded image ${index + 1}`;
}

function UserMessageImageAttachment({
  part,
  index,
  total,
}: {
  part: FilePart;
  index: number;
  total: number;
}) {
  const label = getImageFileLabel(part, index);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={`Open ${label}`}
          className={cn(
            "group relative overflow-hidden rounded-2xl border border-border bg-card/80 text-left shadow-sm transition-colors hover:bg-card",
            total === 1 ? "w-44 sm:w-56" : "w-24 sm:w-32"
          )}
        >
          {/* Authenticated and local preview URLs cannot use Next's image optimizer. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={part.url}
            alt={label}
            className="aspect-square h-auto w-full object-cover"
            loading="lazy"
          />
          <span className="pointer-events-none absolute bottom-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white">
            Open
          </span>
        </button>
      </DialogTrigger>
      <DialogContent
        aria-describedby={undefined}
        className="w-fit max-w-[96vw] border-none bg-background/95 p-2 shadow-xl"
      >
        <DialogTitle className="sr-only">{label}</DialogTitle>
        <div className="flex max-h-[85vh] max-w-[90vw] items-center justify-center overflow-auto rounded-xl bg-muted/20">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={part.url}
            alt={label}
            className="h-auto max-h-[80vh] w-auto max-w-full rounded-lg object-contain"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UserMessageImageGallery({ parts }: { parts: FilePart[] }) {
  return (
    <div className="flex w-full justify-end">
      <div
        className={cn(
          "grid gap-2",
          parts.length === 1 ? "grid-cols-1" : "grid-cols-2"
        )}
      >
        {parts.map((part, index) => (
          <UserMessageImageAttachment
            key={`${part.url}-${index}`}
            part={part}
            index={index}
            total={parts.length}
          />
        ))}
      </div>
    </div>
  );
}

function isGroupedToolCategory(
  category: ToolCategory
): category is GroupedToolCategory {
  return category === "read" || category === "output" || category === "action";
}

function createAssistantPhaseState(): AssistantPhaseState {
  return {
    key: null,
    hasReasoning: false,
    category: null,
    toolParts: [],
  };
}

function ensureAssistantPhaseKey(
  phase: AssistantPhaseState,
  key: string
): string {
  if (!phase.key) {
    phase.key = key;
  }

  return phase.key;
}

function flushAssistantPhase({
  segments,
  phase,
  isActive,
}: {
  segments: AssistantSegment[];
  phase: AssistantPhaseState;
  isActive: boolean;
}) {
  if (phase.toolParts.length > 0 && phase.key && phase.category) {
    segments.push({
      kind: "tool-group",
      key: phase.key,
      category: phase.category,
      parts: [...phase.toolParts],
      isActive,
    });
  } else if (isActive && phase.hasReasoning && phase.key) {
    segments.push({
      kind: "thinking",
      key: phase.key,
    });
  }

  phase.key = null;
  phase.hasReasoning = false;
  phase.category = null;
  phase.toolParts = [];
}

function buildAssistantSegments({
  messageId,
  parts,
  isLoading,
}: {
  messageId: string;
  parts: ChatMessage["parts"] | undefined;
  isLoading: boolean;
}): AssistantSegment[] {
  const segments: AssistantSegment[] = [];
  const phase = createAssistantPhaseState();
  const resolvedAskUserQuestionToolCallIds = new Set<string>();

  for (const part of parts ?? []) {
    const askUserQuestionModel = getAskUserQuestionTranscriptModel(part);
    if (
      askUserQuestionModel &&
      askUserQuestionModel.status !== "pending" &&
      isToolUIPart(part) &&
      getToolKey(part as ToolPart) === "askUserQuestion"
    ) {
      resolvedAskUserQuestionToolCallIds.add((part as ToolPart).toolCallId);
    }
  }

  for (let index = 0; index < (parts ?? []).length; index += 1) {
    const part = parts?.[index];
    if (!part) {
      continue;
    }

    const key = `message-${messageId}-part-${index}`;

    if (part.type === "step-start") {
      continue;
    }

    if (isDataUIPart(part)) {
      const askUserQuestionModel = getAskUserQuestionTranscriptModel(part);
      if (!askUserQuestionModel) {
        continue;
      }

      flushAssistantPhase({
        segments,
        phase,
        isActive: false,
      });

      segments.push({
        kind: "ask-user-summary",
        key,
        model: askUserQuestionModel,
      });
      continue;
    }

    if (isReasoningUIPart(part)) {
      ensureAssistantPhaseKey(phase, key);
      phase.hasReasoning = true;
      continue;
    }

    if (isToolUIPart(part)) {
      const toolPart = part as ToolPart;
      if (getToolKey(toolPart) === "askUserQuestion") {
        const askUserQuestionModel =
          getAskUserQuestionTranscriptModel(toolPart);

        if (
          askUserQuestionModel &&
          !(
            askUserQuestionModel.status === "pending" &&
            resolvedAskUserQuestionToolCallIds.has(toolPart.toolCallId)
          )
        ) {
          flushAssistantPhase({
            segments,
            phase,
            isActive: false,
          });

          segments.push({
            kind: "ask-user-summary",
            key,
            model: askUserQuestionModel,
          });
        }

        continue;
      }

      const category = getToolCategory(toolPart);

      if (
        isGroupedToolCategory(category) &&
        !shouldRenderStandaloneToolPart(toolPart)
      ) {
        if (phase.category && phase.category !== category) {
          flushAssistantPhase({
            segments,
            phase,
            isActive: false,
          });
        }

        ensureAssistantPhaseKey(phase, key);
        phase.category = category;
        phase.toolParts.push(toolPart);
        continue;
      }

      flushAssistantPhase({
        segments,
        phase,
        isActive: false,
      });

      segments.push({
        kind: "tool",
        key,
        part: toolPart,
        isActive: false,
      });
      continue;
    }

    if (!isTextUIPart(part)) {
      continue;
    }

    const cleanedText = sanitizeText(part.text ?? "");
    if (!cleanedText.trim()) {
      continue;
    }

    flushAssistantPhase({
      segments,
      phase,
      isActive: false,
    });

    segments.push({
      kind: "text",
      key,
      text: cleanedText,
    });
  }

  flushAssistantPhase({
    segments,
    phase,
    isActive: isLoading,
  });

  if (!isLoading || segments.length === 0) {
    return segments;
  }

  const lastSegment = segments[segments.length - 1];

  if (
    !lastSegment ||
    lastSegment.kind === "text" ||
    lastSegment.kind === "thinking"
  ) {
    return segments;
  }

  if (lastSegment.kind === "tool-group" && lastSegment.isActive) {
    return segments;
  }

  if (
    lastSegment.kind === "ask-user-summary" &&
    lastSegment.model?.status === "pending"
  ) {
    return segments;
  }

  if (lastSegment.kind === "tool") {
    segments[segments.length - 1] = {
      ...lastSegment,
      isActive: true,
    };
  }

  segments.push({
    kind: "thinking",
    key: `message-${messageId}-trailing-thinking`,
  });

  return segments;
}

function getReadToolGroupSummary({
  parts,
  isActive,
}: {
  parts: ToolPart[];
  isActive: boolean;
}) {
  if (isActive) {
    return {
      label: "Explore",
      count: formatToolCount(parts.length),
    };
  }

  return {
    label: "Explored",
    count: formatToolCount(parts.length),
  };
}

function getToolCardApprovalState({
  part,
  approvalDataByToolCallId,
  pendingToolApprovalId,
}: {
  part: ToolPart;
  approvalDataByToolCallId?: ReturnType<typeof getPendingToolCallApprovalMap>;
  pendingToolApprovalId?: string | null;
}) {
  const approvalData = approvalDataByToolCallId?.get(part.toolCallId) ?? null;

  return {
    approvalData,
    isToolApprovalPending: Boolean(
      approvalData && pendingToolApprovalId === approvalData.approvalId
    ),
  };
}

function ReadToolGroup({
  parts,
  isActive = false,
  approvalDataByToolCallId,
  pendingToolApprovalId,
  onApproveToolCall,
  onDenyToolCall,
}: {
  parts: ToolPart[];
  isActive?: boolean;
  approvalDataByToolCallId?: ReturnType<typeof getPendingToolCallApprovalMap>;
  pendingToolApprovalId?: string | null;
  onApproveToolCall?: (input: ToolCallApprovalActionInput) => void;
  onDenyToolCall?: (input: ToolCallApprovalActionInput) => void;
}) {
  const summary = getReadToolGroupSummary({ parts, isActive });

  return (
    <ToolPhaseGroup
      defaultOpen={isActive}
      renderTrigger={({ isOpen }) => (
        <button
          type="button"
          className="group inline-flex max-w-full items-start justify-start gap-2 self-start text-left text-sm text-foreground/70 transition-colors hover:text-foreground"
        >
          <span className="min-w-0">
            {isActive ? (
              <span className="font-medium">
                <Shimmer>{summary.label}</Shimmer>{" "}
                {summary.count ? (
                  <span className="font-normal text-foreground/50 transition-colors group-hover:text-foreground">
                    {summary.count}
                  </span>
                ) : null}
              </span>
            ) : (
              <span className="font-medium">
                <span className="font-medium">{summary.label}</span>{" "}
                {summary.count ? (
                  <span className="font-normal text-foreground/50 transition-colors group-hover:text-foreground">
                    {summary.count}
                  </span>
                ) : null}
              </span>
            )}
          </span>
          <ChevronDownIcon
            className={cn(
              "mt-0.5 size-4 shrink-0 opacity-0 transition-[opacity,transform] group-hover:opacity-100",
              isOpen && "rotate-180"
            )}
          />
        </button>
      )}
    >
      <div className="flex flex-col gap-2">
        {parts.map((part, index) => {
          const approvalState = getToolCardApprovalState({
            part,
            approvalDataByToolCallId,
            pendingToolApprovalId,
          });

          return (
            <ToolCallCard
              key={`${part.toolCallId ?? part.type}-${index}`}
              part={part}
              title={getToolTitle(part)}
              approvalData={approvalState.approvalData}
              isToolApprovalPending={approvalState.isToolApprovalPending}
              onApproveToolCall={onApproveToolCall}
              onDenyToolCall={onDenyToolCall}
            />
          );
        })}
      </div>
    </ToolPhaseGroup>
  );
}

export function ChatMessageItem({
  chatId,
  message,
  isLoading,
  setMessages,
  regenerate,
  pendingToolApprovalId,
  onApproveToolCall,
  onDenyToolCall,
  requiresScrollPadding,
  disableInitialAnimation = false,
}: {
  chatId: string;
  message: ChatMessage;
  isLoading: boolean;
  setMessages: UseChatHelpers<ChatMessage>["setMessages"];
  regenerate: UseChatHelpers<ChatMessage>["regenerate"];
  pendingToolApprovalId?: string | null;
  onApproveToolCall?: (input: ToolCallApprovalActionInput) => void;
  onDenyToolCall?: (input: ToolCallApprovalActionInput) => void;
  requiresScrollPadding: boolean;
  disableInitialAnimation?: boolean;
}) {
  const [mode, setMode] = useState<"view" | "edit">("view");
  const partsToRender = message.parts ?? [];
  const taggedContext = getTaggedContext(partsToRender);
  const pendingToolCallApprovals = getPendingToolCallApprovalMap(partsToRender);

  let renderedParts: JSX.Element[] = [];

  if (message.role === "assistant") {
    const segments = buildAssistantSegments({
      messageId: message.id,
      parts: partsToRender,
      isLoading,
    });

    renderedParts = segments
      .map((segment) => {
        if (segment.kind === "thinking") {
          return <AssistantThinkingMessage key={segment.key} />;
        }

        if (segment.kind === "tool-group" && segment.category === "read") {
          return (
            <ReadToolGroup
              key={segment.key}
              parts={segment.parts}
              isActive={segment.isActive}
              approvalDataByToolCallId={pendingToolCallApprovals}
              pendingToolApprovalId={pendingToolApprovalId}
              onApproveToolCall={onApproveToolCall}
              onDenyToolCall={onDenyToolCall}
            />
          );
        }

        if (segment.kind === "tool-group" && segment.category === "output") {
          return (
            <OutputToolGroup
              key={segment.key}
              parts={segment.parts}
              isActive={segment.isActive}
              approvalDataByToolCallId={pendingToolCallApprovals}
              pendingToolApprovalId={pendingToolApprovalId}
              onApproveToolCall={onApproveToolCall}
              onDenyToolCall={onDenyToolCall}
            />
          );
        }

        if (segment.kind === "tool-group" && segment.category === "action") {
          return (
            <ActionToolGroup
              key={segment.key}
              parts={segment.parts}
              isActive={segment.isActive}
              approvalDataByToolCallId={pendingToolCallApprovals}
              pendingToolApprovalId={pendingToolApprovalId}
              onApproveToolCall={onApproveToolCall}
              onDenyToolCall={onDenyToolCall}
            />
          );
        }

        if (segment.kind === "tool") {
          const approvalState = getToolCardApprovalState({
            part: segment.part,
            approvalDataByToolCallId: pendingToolCallApprovals,
            pendingToolApprovalId,
          });
          const askUserQuestionModel = getAskUserQuestionTranscriptModel(
            segment.part
          );
          if (askUserQuestionModel) {
            return (
              <AskUserQuestionSummary
                key={segment.key}
                model={askUserQuestionModel}
              />
            );
          }

          return (
            <ToolCallCard
              key={segment.key}
              part={segment.part}
              title={getToolTitle(segment.part)}
              isActive={segment.isActive}
              tone="muted"
              approvalData={approvalState.approvalData}
              isToolApprovalPending={approvalState.isToolApprovalPending}
              onApproveToolCall={onApproveToolCall}
              onDenyToolCall={onDenyToolCall}
            />
          );
        }

        if (segment.kind === "ask-user-summary" && segment.model) {
          return (
            <AskUserQuestionSummary key={segment.key} model={segment.model} />
          );
        }

        if (segment.kind === "text") {
          return (
            <div
              key={segment.key}
              className="flex flex-row gap-2 items-start min-w-0"
            >
              <AIMessage from={message.role} className="max-w-full">
                <MessageContent data-testid="message-content">
                  <MessageResponse>{segment.text}</MessageResponse>
                </MessageContent>
              </AIMessage>
            </div>
          );
        }

        return null;
      })
      .filter((part): part is JSX.Element => part !== null);

    if (renderedParts.length === 0 && isLoading) {
      renderedParts = [
        <AssistantThinkingMessage
          key={`message-${message.id}-loading-fallback`}
        />,
      ];
    }

    if (renderedParts.length === 0) {
      return null;
    }
  } else {
    let hasRenderedEditor = false;
    const imageParts = partsToRender.filter(isImageFilePart);

    if (imageParts.length > 0) {
      renderedParts.push(
        <UserMessageImageGallery
          key={`message-${message.id}-image-gallery`}
          parts={imageParts}
        />
      );
    }

    for (let index = 0; index < partsToRender.length; index += 1) {
      const part = partsToRender[index];
      const key = `message-${message.id}-part-${index}`;

      if (!isTextUIPart(part)) {
        continue;
      }

      if (mode === "edit") {
        if (hasRenderedEditor) continue;
        hasRenderedEditor = true;

        renderedParts.push(
          <div key={key} className="flex flex-row gap-2 items-start">
            <div className="size-8" />
            <ChatMessageEditor
              key={message.id}
              message={message}
              setMode={setMode}
              setMessages={setMessages}
              regenerate={regenerate}
            />
          </div>
        );
        continue;
      }

      const cleanedText = sanitizeText(part.text ?? "");
      if (!cleanedText.trim()) continue;

      renderedParts.push(
        <div key={key} className="flex flex-row gap-2 items-start min-w-0">
          <AIMessage from={message.role} className="max-w-full">
            <MessageContent
              data-testid="message-content"
              className="break-words [overflow-wrap:anywhere]"
            >
              <MessageResponse className="[&_p]:break-words [&_p]:[overflow-wrap:anywhere]">
                {cleanedText}
              </MessageResponse>
            </MessageContent>
          </AIMessage>
        </div>
      );
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        data-testid={`message-${message.role}`}
        className="w-full mx-auto max-w-3xl px-3 group/message"
        initial={disableInitialAnimation ? false : { y: 5, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        data-role={message.role}
      >
        <div
          className={cn(
            "flex gap-4 w-full min-w-0 group-data-[role=user]/message:ml-auto group-data-[role=user]/message:max-w-2xl",
            { "w-full": mode === "edit" }
          )}
        >
          <div
            className={cn("flex flex-col w-full min-w-0 gap-1", {
              "min-h-96": message.role === "assistant" && requiresScrollPadding,
            })}
          >
            <div
              className={cn("flex w-full min-w-0 flex-col", {
                "gap-2": message.role === "user",
                "gap-4": message.role === "assistant",
              })}
            >
              {message.role === "user" && taggedContext && (
                <div className="flex flex-wrap items-center justify-end gap-1.5 pr-1">
                  {taggedContext.map((item) => (
                    <ContextTag
                      key={`${item.type}-${item.id}`}
                      kind={item.type === "dashboard" ? "Dashboard" : "Insight"}
                      label={item.label}
                    />
                  ))}
                </div>
              )}
              {renderedParts}
            </div>

            <ChatMessageActions
              key={`action-${message.id}`}
              chatId={chatId}
              message={message}
              isLoading={isLoading}
              isEditing={mode === "edit"}
              onEdit={
                message.role === "user" ? () => setMode("edit") : undefined
              }
            />
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
